import React from 'react';
import styled from 'styled-components';
import { FiCopy, FiExternalLink, FiLock, FiSettings, FiZap } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { copyTextToClipboard } from '../../blockchain/offlineSigning';
import { useClientConfig } from '../../contexts/ClientConfigContext';
import { hasAssetClassDeployedContract } from '../../utils/assetClassContractUiAccess';

const Wrap = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  background: rgba(196, 181, 253, 0.07);
  border: 1px solid rgba(196, 181, 253, 0.28);
  border-radius: 8px;
  font-size: 0.85rem;

  .head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem 0.65rem;
    margin-bottom: 0.35rem;
  }

  .label {
    color: #c4b5fd;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
  }

  .addr {
    font-family: Consolas, Monaco, monospace;
    color: #e2e8f0;
    font-size: 0.82rem;
    word-break: break-all;
  }

  .meta {
    color: #64748b;
    font-size: 0.78rem;
    line-height: 1.45;
    margin-bottom: 0.5rem;
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 0.4rem;
    font-size: 0.82rem;

    &:last-child {
      margin-bottom: 0;
    }

    .rk {
      color: #94a3b8;
      flex-shrink: 0;
    }

    .rv {
      color: #f1f5f9;
      text-align: right;
      word-break: break-word;
      font-family: ui-monospace, monospace;
    }
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
    margin-top: 0.5rem;
  }

  .or-label {
    font-size: 0.76rem;
    color: #64748b;
    margin: 0 0 0.5rem;
  }
`;

const FieldLabel = styled.label`
  display: block;
  color: #a0a0a0;
  font-size: 0.78rem;
  margin-bottom: 4px;
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(15, 15, 35, 0.9);
  color: #fff;
  font-size: 0.82rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #c4b5fd;
  }
`;

function shortenAddr(addr) {
  const a = String(addr || '').trim();
  if (a.length < 18) return a || '—';
  return `${a.slice(0, 10)}…${a.slice(-8)}`;
}

/**
 * ERC-721 custody vault for fractionalization — one vault record per authorized tokenizer per class.
 * Manual address / lock function and offline deploy live in {@link AssetClassCustodyVaultDeployModal}.
 *
 * @param {{
 *   assetClass: object,
 *   isAdmin: boolean,
 *   authorizedTokenizerId?: string | null,
 *   selectedCustodyTokenizerId?: string,
 *   onCustodyTokenizerSelectionChange?: (id: string) => void,
 *   tokenizerPickerOptions?: { id: string, label: string }[],
 *   tokenPickerOnCard?: boolean,
 *   actionClassName: string,
 *   onOpenCustodyVaultModal?: (authorizedTokenizerId?: string) => void
 * }} props
 */
export function AssetClassCustodyVaultBlock({
  assetClass,
  isAdmin,
  authorizedTokenizerId = null,
  selectedCustodyTokenizerId = '',
  onCustodyTokenizerSelectionChange,
  tokenizerPickerOptions = [],
  tokenPickerOnCard = true,
  actionClassName,
  onOpenCustodyVaultModal
}) {
  const { addressExplorerUrl, explorerConfigured } = useClientConfig();

  const showEditor = isAdmin || Boolean(authorizedTokenizerId);
  const effectiveTokenizerId = isAdmin
    ? String(selectedCustodyTokenizerId || '').trim()
    : String(authorizedTokenizerId || '').trim();
  const showTokenizerPicker = isAdmin && tokenPickerOnCard && tokenizerPickerOptions.length > 0;
  const adminModalOnly = isAdmin && !tokenPickerOnCard;
  const nftDeployed = hasAssetClassDeployedContract(assetClass);

  const vaultAddr = String(assetClass?.custodyVaultContractAddress || '').trim();
  const lockFn = String(assetClass?.custodyVaultLockFunctionName || 'safeTransferFrom').trim();

  const handleCopyVault = async () => {
    if (!vaultAddr) return;
    await copyTextToClipboard(vaultAddr);
    toast.success('Vault address copied');
  };

  const canOpenModal =
    typeof onOpenCustodyVaultModal === 'function' &&
    nftDeployed &&
    (adminModalOnly || Boolean(effectiveTokenizerId));

  const openCustodyModal = () => {
    if (adminModalOnly) {
      onOpenCustodyVaultModal?.();
    } else {
      onOpenCustodyVaultModal?.(effectiveTokenizerId);
    }
  };

  const showDeployVaultCta = canOpenModal && !vaultAddr && !adminModalOnly;
  const showConfigureVaultCta = canOpenModal && Boolean(vaultAddr) && !adminModalOnly;
  const showAdminCustodySetupCta = canOpenModal && adminModalOnly;

  return (
    <Wrap>
      <div className="head">
        <FiLock style={{ color: '#c4b5fd' }} aria-hidden />
        <span className="label">Custody vault (fractionalization)</span>
      </div>
      <div className="meta">
        Each authorized tokenizer has its own vault contract for this class. NFTs are locked into that vault for
        fractionalization; only the vault owner (deployer) can release them on-chain.
      </div>
      {showEditor ? (
        <>
          {showTokenizerPicker ? (
            <div style={{ marginBottom: '0.65rem' }}>
              <FieldLabel>Authorized tokenizer</FieldLabel>
              <SelectInput
                value={selectedCustodyTokenizerId}
                onChange={(e) => onCustodyTokenizerSelectionChange?.(e.target.value)}
                aria-label="Select tokenizer for custody vault"
              >
                <option value="">Select tokenizer…</option>
                {tokenizerPickerOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </SelectInput>
              {!effectiveTokenizerId ? (
                <p className="or-label" style={{ marginTop: '0.35rem' }}>
                  Choose a tokenizer to open the custody vault setup dialog.
                </p>
              ) : null}
            </div>
          ) : null}
          {showAdminCustodySetupCta ? (
            <>
              <div className="meta" style={{ marginBottom: '0.65rem' }}>
                Open the custody vault dialog to review <strong>all approved authorized tokenizers</strong>, select one to
                configure, then deploy <strong>RwaCustodyVault721</strong> or set an existing vault address (same offline
                flow as <strong>Deploy smart contract</strong> for the class NFT).
              </div>
              <div className="actions" style={{ marginTop: 0, marginBottom: 0 }}>
                <button type="button" className={actionClassName} onClick={openCustodyModal}>
                  <FiZap /> Deploy vault smart contract
                </button>
              </div>
            </>
          ) : null}
          {showDeployVaultCta ? (
            <>
              <div className="meta" style={{ marginBottom: '0.65rem' }}>
                No custody vault is linked for this tokenizer yet. Open the setup dialog to deploy{' '}
                <strong>RwaCustodyVault721</strong> with offline signing or enter an existing vault address — the same
                pattern as <strong>Deploy smart contract</strong> for the class NFT.
              </div>
              <div className="actions" style={{ marginTop: 0, marginBottom: 0 }}>
                <button type="button" className={actionClassName} onClick={openCustodyModal}>
                  <FiZap /> Deploy vault smart contract
                </button>
              </div>
            </>
          ) : null}
          {showConfigureVaultCta ? (
            <div className="actions" style={{ marginTop: showDeployVaultCta ? '0.65rem' : 0 }}>
              <button type="button" className={actionClassName} onClick={openCustodyModal}>
                <FiSettings /> Configure custody vault
              </button>
            </div>
          ) : null}
          {vaultAddr ? (
            <>
              <div className="row" style={{ marginTop: '0.65rem' }}>
                <span className="rk">Vault</span>
                <span className="rv" title={vaultAddr}>
                  {shortenAddr(vaultAddr)}
                </span>
              </div>
              <div className="row">
                <span className="rk">Lock function</span>
                <span className="rv">{lockFn || 'safeTransferFrom'}</span>
              </div>
              <div className="actions">
                <button type="button" className={actionClassName} onClick={handleCopyVault}>
                  <FiCopy /> Copy vault
                </button>
                <button
                  type="button"
                  className={actionClassName}
                  disabled={!explorerConfigured}
                  title={explorerConfigured ? 'Open vault in block explorer' : 'Block explorer URL not loaded from API'}
                  onClick={() => {
                    const url = addressExplorerUrl(vaultAddr);
                    if (url) window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <FiExternalLink /> Explorer
                </button>
              </div>
            </>
          ) : null}
        </>
      ) : (
        <>
          <div className="row">
            <span className="rk">Vault</span>
            <span className="rv" title={vaultAddr || undefined}>
              {vaultAddr ? shortenAddr(vaultAddr) : '— not set —'}
            </span>
          </div>
          <div className="row">
            <span className="rk">Lock function</span>
            <span className="rv">{lockFn || 'safeTransferFrom'}</span>
          </div>
          <p className="meta" style={{ marginTop: '0.5rem' }}>
            Sign in as a tokenizer operator (linked to an authorized tokenizer) or an administrator to configure this
            vault.
          </p>
          {vaultAddr ? (
            <div className="actions">
              <button type="button" className={actionClassName} onClick={handleCopyVault}>
                <FiCopy /> Copy vault
              </button>
              <button
                type="button"
                className={actionClassName}
                disabled={!explorerConfigured}
                title={explorerConfigured ? 'Open vault in block explorer' : 'Block explorer URL not loaded from API'}
                onClick={() => {
                  const url = addressExplorerUrl(vaultAddr);
                  if (url) window.open(url, '_blank', 'noopener,noreferrer');
                }}
              >
                <FiExternalLink /> Explorer
              </button>
            </div>
          ) : null}
        </>
      )}
    </Wrap>
  );
}
